import { axiosInstance } from './axios.service'
import { IResponse, ResponseError } from '../types/response.type'
import { PlaybookEndpoints } from '../types/endpoint.type'
import { IPlaybook } from '../types/playbook.type'
import {
  IPlaybookResponse,
  IAllPlaybooksResponse,
  IPlaybook as IPlaybookBackend,
  IRunStateSet,
  IRunStateTsAppend,
  IRunStateUpdate,
} from '../../shared/types/api/playbook.type'
import { IMessageResponse } from '../../shared/types/api/message.type'

const feToBe = (playbook: IPlaybook): Omit<IPlaybookBackend, 'runs' | 'status'> => {
  const { runs, status, ...props } = playbook // Don't send runs or derived properties
  return {
    ...props,
    created_at: playbook.created_at?.toJSON(),
    nodes: playbook.nodes.map((node) => ({
      ...node,
      id: node.id,
      type: node.type!,
    })),
    edges: playbook.edges.map((edge) => ({
      ...edge,
      label: edge.data?.label,
    })),
  }
}

/*
 * Takes a playbook from the backend and converts it to the format we want on the frontend.
 */
const beToFe = (playbook: IPlaybookBackend): IPlaybook => {
  return {
    ...playbook,
    created_at: new Date(Date.parse(playbook.created_at)),
    nodes: playbook.nodes?.map((node) => ({
      ...node,
      id: node.id.toString(),
    })),
    edges: playbook.edges?.map((edge) => ({
      ...edge,
      data: { label: edge.label },
    })),
  }
}

export const getAllPlaybooks = async (
  search: string,
  page: number,
  pageSize: number,
): Promise<IResponse<IPlaybook[]>> => {
  const resp = await axiosInstance.get<IAllPlaybooksResponse>(PlaybookEndpoints.GET_ALL + `?search=${search || ''}`, {
    params: {
      skip: page,
      take: pageSize,
    },
  })

  if (resp.data.success) {
    return {
      ...resp.data,
      data: resp.data.data.map(beToFe),
    }
  } else {
    return resp.data
  }
}

export const getOnePlaybook = async (id: string): Promise<IResponse<IPlaybook>> => {
  const resp = await axiosInstance.get<IPlaybookResponse>(PlaybookEndpoints.GET_ONE + id)

  return {
    ...resp.data,
    data: resp.data.success ? beToFe(resp.data.data) : undefined,
  }
}

export const createPlaybook = async (playbook: { name: string; slug: string }): Promise<IResponse<IPlaybook>> => {
  const resp = await axiosInstance.post<IPlaybookResponse>(PlaybookEndpoints.CREATE, playbook)

  return {
    ...resp.data,
    data: resp.data.success ? beToFe(resp.data.data) : undefined,
  }
}

export const updatePlaybook = async (playbook: IPlaybook, isCreating?: boolean): Promise<IResponse<IPlaybook>> => {
  const resp = await axiosInstance.put<IPlaybookResponse>(PlaybookEndpoints.UPDATE + playbook.id, {
    ...feToBe(playbook),
    isCreating,
  })

  if (resp.data.success) {
    return {
      ...resp.data,
      data: beToFe(resp.data.data),
    }
  } else {
    throw new ResponseError(resp.data.message)
  }
}

export const deletePlaybook = async (id: number): Promise<IMessageResponse> => {
  const resp = await axiosInstance.delete<IMessageResponse>(PlaybookEndpoints.DELETE + id)

  return resp.data
}

export const downloadPdf = async (subject: string, requestId: number, locations: string[], dateRange: number[]) => {
  const resp = await axiosInstance.post(PlaybookEndpoints.DOWNLOAD_PDF, {
    subject,
    locations,
    requestId,
    dateRange,
  })

  return resp.data
}

export const runPlaybook = async (playbook_id: number): Promise<IResponse<IPlaybook>> => {
  const resp = await axiosInstance.post<IPlaybookResponse>(
    PlaybookEndpoints.RUN.replace(':id', playbook_id.toString()),
    {
      params: {},
    },
  )

  return {
    ...resp.data,
    data: resp.data.success ? beToFe(resp.data.data) : undefined,
  }
}

export const startPlaybook = async (playbook_id: number | string, data: any): Promise<IResponse<IPlaybook>> => {
  const resp = await axiosInstance.post<IResponse<IPlaybook>>(
    PlaybookEndpoints.START_PLAYBOOK.replace(':id', playbook_id.toString()),
    data,
  )

  return resp.data
}

export const getPlaybookReport = async (playbook_id: number | string, report: string, filter: object): Promise<any> => {
  const resp = await axiosInstance.get<any>(
    PlaybookEndpoints.GET_REPORT.replace(':id', playbook_id.toString()).replace(':report', report.toString()),
    {
      params: {
        ...filter,
      },
    },
  )

  return resp.data
}

export const exportPlaybookYaml = async (playbookId: number): Promise<void> => {
  const resp = await axiosInstance.get(PlaybookEndpoints.EXPORT_YAML.replace(':id', playbookId.toString()), {
    responseType: 'blob',
  })

  const url = window.URL.createObjectURL(resp.data)
  const a = document.createElement('a')
  a.href = url
  const disposition = resp.headers['content-disposition'] || ''
  const filenameMatch = disposition.match(/filename=(.+)/)
  a.download = filenameMatch ? filenameMatch[1] : 'playbook.yaml'
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

export const importPlaybookYaml = async (yaml: string): Promise<IResponse<IPlaybook>> => {
  const resp = await axiosInstance.put<IPlaybookResponse>(PlaybookEndpoints.IMPORT_YAML, { yaml })

  return {
    ...resp.data,
    data: resp.data.success ? beToFe(resp.data.data) : undefined,
  }
}

export const stopPlaybook = async (playbook_id: number): Promise<IResponse<IPlaybook>> => {
  const resp = await axiosInstance.delete<IPlaybookResponse>(
    PlaybookEndpoints.STOP.replace(':id', playbook_id.toString()),
  )

  return {
    ...resp.data,
    data: resp.data.success ? beToFe(resp.data.data) : undefined,
  }
}

type RunState = Record<string, any>

const applyRunStateSet = (old: RunState, upd: IRunStateSet) => ({
  ...old,
  [upd.key]: upd.val,
})

const applyRunStateTsAppend = (old: RunState, upd: IRunStateTsAppend) => {
  const tsarr: number[] = [...(old[upd.tskey] || [])]
  tsarr.splice(0, upd.too_old)
  tsarr.push(upd.ts)
  const ret = {
    ...old,
    [upd.tskey]: tsarr,
  }

  for (const attr of Object.keys(upd.vals)) {
    const ar = ret[attr]?.vals || []
    ar.splice(0, upd.too_old)
    ar.push(upd.vals[attr])
    ret[attr] = { vals: ar, tskey: upd.tskey }
  }

  return ret
}

export const applyRunStateUpdate = (old: RunState, upd: IRunStateUpdate) => {
  switch (upd.op) {
    case 'set':
      return applyRunStateSet(old, upd)
    case 'tsappend':
      return applyRunStateTsAppend(old, upd)
    default:
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _exhaustiveCheck: never = upd
      throw new Error(`Unhandled case: ${JSON.stringify(upd)}`)
  }
}
